import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format, parse } from "date-fns";
import { Pencil, Trash2, Archive, ArchiveRestore } from "lucide-react";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";
import { BonusDTO, BonusType, TopPerformerDTO } from "@/types/bonus";
import { BackLink } from "@/components/brand/BackLink";
import { BonusTypePill } from "@/components/admin/bonuses/BonusTypePill";
import { StatusPill } from "@/components/brand/StatusPill";

export default function BonusDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bonus, setBonus] = useState<BonusDTO | null>(null);
  const [topPerformers, setTopPerformers] = useState<TopPerformerDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axiosInstance
      .get(`/bonus/${id}`)
      .then((res) => {
        const data: BonusDTO = res.data;
        setBonus(data);
        if (data.bonusType === BonusType.INCENTIVE) {
          return axiosInstance.get(`/bonus/${id}/top-performers`);
        }
        return null;
      })
      .then((res) => {
        if (res) setTopPerformers(res.data);
      })
      .catch(() => {
        setError("Failed to load bonus program");
        toast({
          title: "Error",
          description: "Failed to load bonus program details.",
          variant: "destructive",
        });
      })
      .finally(() => setLoading(false));
  }, [id, toast]);

  const handleDelete = async () => {
    if (!id || !window.confirm("Delete this bonus program?")) return;
    try {
      await axiosInstance.delete(`/bonus/${id}`);
      toast({
        title: "Deleted",
        description: "Bonus program deleted.",
        variant: "success",
      });
      navigate("/admin/bonuses");
    } catch {
      toast({
        title: "Error",
        description: "Failed to delete.",
        variant: "destructive",
      });
    }
  };

  const handleSetArchived = async (archived: boolean) => {
    if (!id) return;
    try {
      await axiosInstance.put(`/bonus/${id}/archive?archived=${archived}`);
      toast({
        title: archived ? "Archived" : "Unarchived",
        description: archived
          ? "Bonus program archived."
          : "Bonus program restored.",
        variant: "success",
      });
      navigate("/admin/bonuses");
    } catch {
      toast({
        title: "Error",
        description: "Failed to update archive status.",
        variant: "destructive",
      });
    }
  };

  const handleAward = async (userId: string) => {
    try {
      await axiosInstance.post("/earned-bonus", { bonusId: id, userId });
      toast({
        title: "Awarded",
        description: "Earned bonus record created.",
        variant: "success",
      });
      const res = await axiosInstance.get(`/bonus/${id}/top-performers`);
      setTopPerformers(res.data);
    } catch (err) {
      toast({
        title: "Error",
        description: err?.response?.data?.message ?? "Failed to award bonus.",
        variant: "destructive",
      });
    }
  };

  const handleMarkPaid = async (earnedBonusId: string) => {
    try {
      await axiosInstance.put(`/earned-bonus/${earnedBonusId}/mark-paid`);
      toast({
        title: "Marked Paid",
        description: "Bonus marked as paid.",
        variant: "success",
      });
      const res = await axiosInstance.get(`/bonus/${id}/top-performers`);
      setTopPerformers(res.data);
    } catch {
      toast({
        title: "Error",
        description: "Failed to mark as paid.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="h-40 flex items-center justify-center">Loading...</div>
      </div>
    );
  }

  if (error || !bonus) {
    return (
      <div className="container mx-auto p-6">
        <div className="h-40 flex items-center justify-center text-destructive">
          {error || "Failed to load bonus program"}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <BackLink to="/admin/bonuses">Back to Bonuses</BackLink>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">{bonus.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <BonusTypePill type={bonus.bonusType} />
              {bonus.archived && (
                <StatusPill tone="warning">Archived</StatusPill>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!bonus.archived && (
            <Link to={`/admin/bonuses/${id}/edit`}>
              <Button variant="outline">
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </Link>
          )}
          {bonus.canDelete ? (
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          ) : bonus.archived ? (
            <Button variant="outline" onClick={() => handleSetArchived(false)}>
              <ArchiveRestore className="mr-2 h-4 w-4" />
              Unarchive
            </Button>
          ) : (
            <Button variant="outline" onClick={() => handleSetArchived(true)}>
              <Archive className="mr-2 h-4 w-4" />
              Archive
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Start Date</p>
          <p className="font-medium">
            {format(
              parse(bonus.startDate, "yyyy-MM-dd", new Date()),
              "MMM d, yyyy",
            )}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">End Date</p>
          <p className="font-medium">
            {format(
              parse(bonus.endDate, "yyyy-MM-dd", new Date()),
              "MMM d, yyyy",
            )}
          </p>
        </div>
        {bonus.requiredParties != null && (
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Required Parties</p>
            <p className="font-medium">{bonus.requiredParties}</p>
          </div>
        )}
        {bonus.dollarAmount != null && (
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Bonus Amount</p>
            <p className="font-medium">${bonus.dollarAmount}</p>
          </div>
        )}
        {bonus.topN != null && (
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Top N Performers</p>
            <p className="font-medium">{bonus.topN}</p>
          </div>
        )}
        {bonus.notes && (
          <div className="space-y-1 md:col-span-2">
            <p className="text-sm text-muted-foreground">Notes</p>
            <p className="font-medium">{bonus.notes}</p>
          </div>
        )}
      </div>

      {/* Type-specific section */}
      {bonus.bonusType === BonusType.INCENTIVE && (
        <div className="mt-8">
          <h2 className="font-display text-2xl font-semibold mb-4">
            Top Performers
          </h2>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rank</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Parties Worked</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topPerformers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground"
                    >
                      No data available yet for this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  topPerformers.map((p) => (
                    <TableRow key={p.userId}>
                      <TableCell className="font-bold">#{p.rank}</TableCell>
                      <TableCell>{p.userName}</TableCell>
                      <TableCell>{p.partiesWorked}</TableCell>
                      <TableCell>
                        {p.paid ? (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-success/10 text-success">
                            Paid
                          </span>
                        ) : p.earnedBonusExists ? (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-info/10 text-info">
                            Awarded
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-warning/10 text-warning">
                            Pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {!p.earnedBonusExists && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAward(p.userId)}
                          >
                            Award
                          </Button>
                        )}
                        {p.earnedBonusExists && !p.paid && p.earnedBonusId && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleMarkPaid(p.earnedBonusId!)}
                          >
                            Mark Paid
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {(bonus.bonusType === BonusType.SEASONAL ||
        bonus.bonusType === BonusType.REFERRAL) && (
        <div className="mt-8">
          <h2 className="font-display text-2xl font-semibold mb-4">
            Earned Bonus Records
          </h2>
          <p className="text-muted-foreground mb-4">
            View all earned bonus records for this program, including payment
            status.
          </p>
          <Link to={`/admin/earned-bonuses?bonusId=${id}`}>
            <Button variant="outline">View Earned Bonus Records</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
